import { useEffect, useRef, useState } from 'react';
import { ETERNITY } from '@/content/eternity';
import { track } from '@/lib/analytics';

/**
 * Site-wide easter eggs:
 *  - styled console greeting (curious people make good crew)
 *  - Konami code → a brief "memory decay" glitch across the page
 *  - type-to-trigger words: "wake up" (Matrix terminal), "matrix" (digital
 *    rain), "forever" (a line), "remember" (the decay glitch)
 * Page-specific eggs live elsewhere (logline YES/NO + idle/tears on /eternity,
 * red/blue pill on /remember).
 */

let consoleShown = false;

const KONAMI = [
    'ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown',
    'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a',
];

type Message = { lines: string[]; variant: 'terminal' | 'brand' | 'obsession' } | null;

/** Types the terminal lines on char-by-char, line by line, with a blinking
 *  cursor — like a real console printing. */
const TerminalType = ({ lines }: { lines: string[] }) => {
    const reduce =
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const [shown, setShown] = useState<string[]>(reduce ? lines : ['']);

    useEffect(() => {
        if (reduce) return;
        let li = 0;
        let ci = 0;
        let cancelled = false;
        const out = [''];
        let timer = 0;
        const tick = () => {
            if (cancelled || li >= lines.length) return;
            const line = lines[li];
            if (ci < line.length) {
                out[li] = line.slice(0, ++ci);
                setShown([...out]);
                timer = window.setTimeout(tick, 40);
            } else {
                li += 1;
                ci = 0;
                if (li < lines.length) {
                    out[li] = '';
                    setShown([...out]);
                    timer = window.setTimeout(tick, 360);
                }
            }
        };
        timer = window.setTimeout(tick, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [lines, reduce]);

    return (
        <>
            {shown.map((t, i) => (
                <p key={i}>
                    {t}
                    {i === shown.length - 1 && <span className="egg-cursor" aria-hidden="true" />}
                </p>
            ))}
        </>
    );
};

const EasterEggs = () => {
    const [decaying, setDecaying] = useState(false);
    const [message, setMessage] = useState<Message>(null);
    const [rain, setRain] = useState(false);
    const rainRef = useRef<HTMLCanvasElement>(null);

    // Console greeting — once per load.
    useEffect(() => {
        if (consoleShown) return;
        consoleShown = true;
        const title = 'font:700 30px "DIN Condensed","Barlow Condensed",sans-serif;letter-spacing:12px;color:#2b7d92;';
        const line = 'color:#7a86a8;font-style:italic;font-size:13px;';
        const body = 'color:#9aa3bd;font-size:13px;line-height:1.6;';
        // eslint-disable-next-line no-console
        console.log(
            `%cETERNITY\n%cHe had eternity to remember her. It wasn't enough.\n\n%cYou found the console. Curious people make good crew.\nSay hi → hello@thismx.com  ·  (try typing "wake up")`,
            title, line, body
        );
    }, []);

    // Konami code → memory-decay glitch.
    // Type-to-trigger words.
    useEffect(() => {
        let konami: string[] = [];
        let buf = '';
        const triggerDecay = () => setDecaying(true);
        const onKey = (e: KeyboardEvent) => {
            // konami
            const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
            konami.push(k);
            if (konami.length > KONAMI.length) konami = konami.slice(-KONAMI.length);
            if (konami.length === KONAMI.length && KONAMI.every((x, i) => x === konami[i])) {
                konami = [];
                track('egg_konami');
                triggerDecay();
            }
            // typed words
            if (/^[a-z]$/i.test(e.key)) {
                buf = (buf + e.key.toLowerCase()).slice(-12);
                if (buf.endsWith('wakeup')) { buf = ''; track('egg_wakeup'); setMessage({ lines: ETERNITY.eggs.wakeUp, variant: 'terminal' }); }
                else if (buf.endsWith('matrix')) { buf = ''; track('egg_matrix'); setRain(true); }
                else if (buf.endsWith('forever')) { buf = ''; track('egg_forever'); setMessage({ lines: [ETERNITY.eggs.forever], variant: 'brand' }); }
                else if (buf.endsWith('remember')) { buf = ''; track('egg_remember'); triggerDecay(); }
                else if (buf.endsWith('forgotten')) { buf = ''; track('egg_forgotten'); window.location.assign('/archive'); }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    // The One Wish Willow (Obsession): the hidden toy on /eternity dispatches
    // this when its wish is granted — crimson curse + a corruption glitch.
    useEffect(() => {
        const onWish = () => {
            setMessage({ lines: ETERNITY.eggs.obsession, variant: 'obsession' });
            setDecaying(true);
        };
        window.addEventListener('mx:wish', onWish);
        return () => window.removeEventListener('mx:wish', onWish);
    }, []);

    useEffect(() => {
        if (!decaying) return;
        const root = document.documentElement;
        root.classList.add('egg-decay');
        const t = window.setTimeout(() => {
            root.classList.remove('egg-decay');
            setDecaying(false);
        }, 1500);
        return () => { window.clearTimeout(t); root.classList.remove('egg-decay'); };
    }, [decaying]);

    // auto-dismiss the message overlay (terminal types out; obsession reads as 3 lines)
    const msgDuration = message?.variant === 'terminal' ? 6200 : message?.variant === 'obsession' ? 5400 : 4200;
    useEffect(() => {
        if (!message) return;
        const t = window.setTimeout(() => setMessage(null), msgDuration);
        return () => window.clearTimeout(t);
    }, [message, msgDuration]);

    // matrix rain
    useEffect(() => {
        if (!rain) return;
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const stop = window.setTimeout(() => setRain(false), reduce ? 1200 : 3000);
        if (reduce) return () => window.clearTimeout(stop);
        const canvas = rainRef.current;
        if (!canvas) return () => window.clearTimeout(stop);
        const ctx = canvas.getContext('2d');
        if (!ctx) return () => window.clearTimeout(stop);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const W = (canvas.width = window.innerWidth * dpr);
        const H = (canvas.height = window.innerHeight * dpr);
        const fontSize = 16 * dpr;
        const cols = Math.floor(W / fontSize);
        const drops = Array.from({ length: cols }, () => Math.random() * -H);
        const glyphs = 'ｱｲｳｴｵｶｷｸ0123456789ETRNIY<>/\\=+*'.split('');
        let raf = 0;
        const draw = () => {
            ctx.fillStyle = 'rgba(0, 8, 6, 0.18)';
            ctx.fillRect(0, 0, W, H);
            ctx.font = `${fontSize}px monospace`;
            for (let i = 0; i < cols; i++) {
                const g = glyphs[Math.floor(Math.random() * glyphs.length)];
                ctx.fillStyle = Math.random() > 0.96 ? '#bdffe6' : '#2bd98a';
                ctx.fillText(g, i * fontSize, drops[i]);
                drops[i] += fontSize;
                if (drops[i] > H && Math.random() > 0.975) drops[i] = Math.random() * -100;
            }
            raf = requestAnimationFrame(draw);
        };
        draw();
        return () => { window.clearTimeout(stop); cancelAnimationFrame(raf); };
    }, [rain]);

    return (
        <>
            {decaying && <div className="egg-scanlines" aria-hidden="true" />}

            {rain && <canvas ref={rainRef} className="egg-matrix" aria-hidden="true" />}

            {message && (
                <div
                    className={`egg-msg egg-msg-${message.variant}`}
                    style={{ animationDuration: `${msgDuration}ms` }}
                    aria-hidden="true"
                >
                    <div className="egg-msg-inner">
                        {message.variant === 'terminal' ? (
                            <TerminalType lines={message.lines} />
                        ) : (
                            message.lines.map((l, i) => (
                                <p key={i} style={{ animationDelay: `${i * 0.7}s` }}>{l}</p>
                            ))
                        )}
                    </div>
                </div>
            )}

            <style>{`
                .egg-matrix {
                    position: fixed; inset: 0; z-index: 9998;
                    width: 100vw; height: 100vh;
                    pointer-events: none;
                    animation: egg-fade 3s ease forwards;
                }
                .egg-msg {
                    position: fixed; inset: 0; z-index: 9999;
                    display: flex; align-items: center; justify-content: center;
                    text-align: center; padding: 2rem;
                    pointer-events: none;
                    animation: egg-fade 4.2s ease forwards;
                }
                .egg-msg-terminal { background: rgba(0, 6, 4, 0.92); }
                .egg-msg-brand { background: rgba(5, 6, 15, 0.86); }
                /* Obsession (2026) — crimson horror-romance */
                .egg-msg-obsession {
                    background:
                        radial-gradient(60% 50% at 50% 50%, rgba(60, 2, 10, 0.5), transparent),
                        rgba(7, 2, 4, 0.93);
                }
                .egg-msg-obsession .egg-msg-inner { max-width: 32ch; }
                .egg-msg-obsession .egg-msg-inner p {
                    font-family: var(--font-display);
                    text-transform: uppercase;
                    font-size: clamp(1.3rem, 4.6vw, 2.7rem);
                    line-height: 1.06;
                    letter-spacing: 0.01em;
                    color: #e7c9cf;
                    text-shadow: 0 0 26px rgba(196, 16, 46, 0.45);
                }
                .egg-msg-obsession .egg-msg-inner p:last-child {
                    margin-top: 0.7em;
                    font-family: var(--font-mono);
                    text-transform: uppercase;
                    font-size: clamp(0.62rem, 1.8vw, 0.92rem);
                    letter-spacing: 0.32em;
                    color: #c4102e;
                    text-shadow: 0 0 18px rgba(196, 16, 46, 0.7);
                }
                .egg-msg-inner p {
                    margin: 0 0 0.5em;
                    opacity: 0;
                    animation: egg-type 0.05s steps(1) forwards;
                }
                .egg-msg-terminal .egg-msg-inner { text-align: left; }
                .egg-msg-terminal .egg-msg-inner p {
                    font-family: var(--font-mono);
                    font-size: clamp(1rem, 3vw, 1.7rem);
                    letter-spacing: 0.04em;
                    color: #2bd98a;
                    text-shadow: 0 0 12px rgba(43, 217, 138, 0.5);
                    opacity: 1;
                    animation: none;
                    min-height: 1.2em;
                }
                .egg-cursor {
                    display: inline-block;
                    width: 0.55em;
                    height: 1.05em;
                    margin-left: 0.12em;
                    background: currentColor;
                    vertical-align: -0.16em;
                    animation: egg-blink 0.9s steps(1) infinite;
                }
                @keyframes egg-blink { 50% { opacity: 0; } }
                .egg-msg-brand .egg-msg-inner p {
                    font-family: var(--font-display);
                    text-transform: uppercase;
                    font-size: clamp(1.4rem, 5vw, 3rem);
                    letter-spacing: 0.02em;
                    color: var(--et-text, #e8ecff);
                }
                @keyframes egg-type { to { opacity: 1; } }
                @keyframes egg-fade {
                    0% { opacity: 0; }
                    8%, 82% { opacity: 1; }
                    100% { opacity: 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .egg-matrix, .egg-msg { animation: egg-fade-rm 1.2s ease forwards; }
                    .egg-msg-inner p { animation: none; opacity: 1; }
                    @keyframes egg-fade-rm { 0%,100% { opacity: 0; } 20%,80% { opacity: 1; } }
                }
            `}</style>
        </>
    );
};

export default EasterEggs;
